/**
 * Whether the "How did you hear about us?" step asks a follow-up question, given the chosen lead
 * source.
 *
 * Pure and separate from the component because these rules gate every lead source, not just the two
 * LLS-530 touches: they used to live in one long boolean expression inside the template, where a
 * change to one branch could silently alter the others.
 */
export class LeadSourceVisibility {
  static readonly AGENT_LEAD_SOURCES = ['Agents', 'Real Estate Agent'];

  /**
   * Lead sources that stand on their own. "Returning Client" is here by design - the borrower is a
   * returning client of this lender, so there is nobody to name as a referrer.
   */
  static readonly WITHOUT_SECONDARY_QUESTION = [
    'Radio',
    'Letter in the Mail',
    'Mailer',
    'Returning Client',
  ];

  static isAgentLeadSource = (leadSourceType: string): boolean =>
    LeadSourceVisibility.AGENT_LEAD_SOURCES.includes(leadSourceType);

  /**
   * Whether the free-text "who/what was it" box is shown. Unchanged for every pre-existing lead
   * source, including Personal Referral, which keeps the same text box it has always had; the only
   * new behaviour is that Returning Client asks nothing.
   */
  static showsReferralTextInput = (state: LeadSourceVisibilityState): boolean => {
    if (!state.leadSourceType) {
      return false;
    }

    if (LeadSourceVisibility.isAgentLeadSource(state.leadSourceType)) {
      return (
        !state.pickAgentReferralFromAList ||
        state.useManualAgentEntry ||
        state.leadSourceAgent == -1
      );
    }

    return !LeadSourceVisibility.WITHOUT_SECONDARY_QUESTION.includes(state.leadSourceType);
  };
}

export interface LeadSourceVisibilityState {
  leadSourceType: string;
  pickAgentReferralFromAList: boolean;
  useManualAgentEntry: boolean;
  leadSourceAgent: number;
}
